import { supabase } from "../lib/supabase";
import { Invoice } from "../types/invoice";
import { SupabaseClient } from "@supabase/supabase-js";

// Type cast supabase to avoid TypeScript errors
const typedSupabase = supabase as unknown as SupabaseClient;

/**
 * Fetch all invoices with their lines and booking details
 */
export const fetchInvoices = async () => {
  try {
    const { data: invoices, error: invoicesError, count } = await typedSupabase
      .from("booking_sales_invoice")
      .select(
        `
        *,
        booking:bookings(
          id, 
          reference,
          status,
          transport_type_info:booking_transport_types(*),
          company_info:company(
            *,
            pricing_payment_term:pricing_payment_terms(*)
          )
        )
      `,
        { count: "exact" }
      )
      .order("created_at", { ascending: false });

    if (invoicesError) throw invoicesError;

    // Fetch lines for all invoices in a single query
    const invoiceIds = invoices?.map(inv => inv.id).filter(Boolean) || [];
    
    let allLines: any[] = [];
    if (invoiceIds.length > 0) {
      const { data: lines, error: linesError } = await typedSupabase
        .from("booking_sales_invoice_line")
        .select("*")
        .in("invoice_id", invoiceIds)
        .order("id");

      if (linesError) throw linesError;
      allLines = lines || [];
    }

    // Group lines by invoice_id and attach to invoices
    const linesGrouped = allLines.reduce((acc, line) => {
      if (!acc[line.invoice_id]) {
        acc[line.invoice_id] = [];
      }
      acc[line.invoice_id].push(line);
      return acc;
    }, {} as Record<number, any[]>);

    // Attach lines to each invoice
    const invoicesWithLines = invoices?.map(invoice => ({
      ...invoice,
      lines: linesGrouped[invoice.id!] || []
    })) || [];

    return { data: invoicesWithLines, count, error: null };
  } catch (error) {
    console.error("Error fetching invoices:", error);
    return { data: [], count: 0, error };
  }
};

/**
 * Fetch a single invoice by id with its lines
 */
export const fetchInvoiceById = async (id: number) => {
  try {
    // Fetch the invoice
    const { data: invoice, error: invoiceError } = await typedSupabase
      .from("booking_sales_invoice")
      .select(
        `
        *,
        booking:bookings(
          *,
          transport_type_info:booking_transport_types(*),
          company_info:company(
            *,
            pricing_payment_term:pricing_payment_terms(*)
          ),
          origin_port_info:booking_origin_port(*),
          destination_port_info:booking_destination_port(*)
        )
      `
      )
      .eq("id", id)
      .single();

    if (invoiceError) throw invoiceError;

    // Fetch the invoice lines
    const { data: lines, error: linesError } = await typedSupabase
      .from("booking_sales_invoice_line")
      .select("*")
      .eq("invoice_id", id)
      .order("id");

    if (linesError) throw linesError;

    return { 
      data: { ...invoice, lines: lines || [] }, 
      error: null 
    };
  } catch (error) {
    console.error("Error fetching invoice:", error);
    return { data: null, error };
  }
};

/**
 * Create a new invoice
 */
export const createInvoice = async (invoice: Invoice) => {
  try {
    if (invoice.booking_id == null) {
      return {
        data: null,
        error: new Error("booking_id is required to create an invoice"),
      };
    }

    const { data: invoiceData, error: invoiceError } = await typedSupabase
      .from("booking_sales_invoice")
      .insert({
        booking_id: invoice.booking_id,
        company_id: invoice.company_id ?? null,
        currency: invoice.currency,
        status: invoice.status || 'final',
        payment_term: invoice.payment_term,
        custom_payment_terms: invoice.custom_payment_terms,
      })
      .select()
      .single();

    if (invoiceError) throw invoiceError;

    const invoiceLines = invoice.lines
      .filter(line => line.description || line.unit_price)
      .map(line => ({
        description: line.description,
        quantity: line.quantity || 0,
        unit_price: line.unit_price || 0,
        vat_percentage: line.vat_percentage ?? 21,
        cost_type: line.cost_type,
        invoice_id: invoiceData.id,
        booking_id: invoice.booking_id
      }));

    let linesData: any[] = [];
    if (invoiceLines.length > 0) {
      const { data, error: linesError } = await typedSupabase
        .from("booking_sales_invoice_line")
        .insert(invoiceLines)
        .select();

      if (linesError) throw linesError;
      linesData = data || [];
    }

    return { 
      data: { ...invoiceData, lines: linesData }, 
      error: null 
    };
  } catch (error) {
    console.error("Error creating invoice:", error);
    return { data: null, error };
  }
};

/**
 * Finalize a draft invoice (set status to 'final')
 */
export const finalizeInvoice = async (id: number) => {
  try {
    const { data, error } = await typedSupabase
      .from("booking_sales_invoice")
      .update({ status: 'final' })
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error("Error finalizing invoice:", error);
    return { data: null, error };
  }
};

/**
 * Update an existing invoice
 */
export const updateInvoice = async (id: number, invoice: Partial<Invoice>) => {
  try {
    const updatePayload: Record<string, unknown> = {
      booking_id: invoice.booking_id,
      company_id: invoice.company_id,
      currency: invoice.currency,
    };
    if (invoice.payment_term !== undefined) updatePayload.payment_term = invoice.payment_term;
    if (invoice.custom_payment_terms !== undefined) updatePayload.custom_payment_terms = invoice.custom_payment_terms;
    if (invoice.status !== undefined) updatePayload.status = invoice.status;

    const { data: invoiceData, error: invoiceError } = await typedSupabase
      .from("booking_sales_invoice")
      .update(updatePayload)
      .eq("id", id)
      .select()
      .single();

    if (invoiceError) throw invoiceError;

    // Handle invoice lines if provided
    if (invoice.lines) {
      console.log('🔍 DEBUG: Starting line update process');
      console.log('📝 Lines to process:', JSON.stringify(invoice.lines, null, 2));
      
      // Fetch existing lines to preserve moneybird_invoice_line_id
      const { data: existingLines, error: fetchError } = await typedSupabase
        .from("booking_sales_invoice_line")
        .select("*")
        .eq("invoice_id", id)
        .order("id");

      if (fetchError) throw fetchError;
      
      console.log('📋 Existing lines from DB:', JSON.stringify(existingLines, null, 2));

      const updatedLines = [];
      const existingLinesMap = new Map((existingLines || []).map(line => [line.id, line]));
      
      console.log('🗺️ Existing lines map keys:', Array.from(existingLinesMap.keys()));

      // Process each line in the updated invoice
      for (let i = 0; i < invoice.lines.length; i++) {
        const newLine = invoice.lines[i];
        console.log(`\n📝 Processing line ${i}:`, JSON.stringify(newLine, null, 2));
        console.log(`🔍 Line ID: ${newLine.id}, Type: ${typeof newLine.id}`);
        console.log(`✅ Has ID: ${!!newLine.id}, ID > 0: ${newLine.id && newLine.id > 0}, Exists in map: ${newLine.id && existingLinesMap.has(newLine.id)}`);
        
        // If line has an ID and exists in the database, update it
        if (newLine.id && newLine.id > 0 && existingLinesMap.has(newLine.id)) {
          const existingLine = existingLinesMap.get(newLine.id);
          
          const { data: updatedLine, error: updateError } = await typedSupabase
            .from("booking_sales_invoice_line")
            .update({
              description: newLine.description,
              quantity: newLine.quantity || 0,
              unit_price: newLine.unit_price || 0,
              vat_percentage: newLine.vat_percentage ?? 21,
              cost_type: newLine.cost_type,
              // Preserve moneybird_invoice_line_id
              moneybird_invoice_line_id: existingLine?.moneybird_invoice_line_id
            })
            .eq("id", newLine.id)
            .select()
            .single();

          if (updateError) throw updateError;
          updatedLines.push(updatedLine);
          existingLinesMap.delete(newLine.id);
        } else {
          // Create new line (explicitly exclude id field)
          const { data: createdLine, error: createError } = await typedSupabase
            .from("booking_sales_invoice_line")
            .insert({
              invoice_id: id,
              booking_id: invoice.booking_id,
              description: newLine.description,
              quantity: newLine.quantity || 0,
              unit_price: newLine.unit_price || 0,
              vat_percentage: newLine.vat_percentage ?? 21,
              cost_type: newLine.cost_type,
              // Explicitly do not include 'id' field for new records
            })
            .select()
            .single();

          if (createError) throw createError;
          updatedLines.push(createdLine);
        }
      }

      // Delete any remaining existing lines that weren't updated
      const linesToDelete = Array.from(existingLinesMap.keys());
      if (linesToDelete.length > 0) {
        const { error: deleteError } = await typedSupabase
          .from("booking_sales_invoice_line")
          .delete()
          .in("id", linesToDelete);

        if (deleteError) throw deleteError;
      }

      return { 
        data: { ...invoiceData, lines: updatedLines }, 
        error: null 
      };
    }

    return { data: invoiceData, error: null };
  } catch (error) {
    console.error("Error updating invoice:", error);
    return { data: null, error };
  }
};

/**
 * Delete an invoice
 */
export const deleteInvoice = async (id: number) => {
  try {
    // Note: Due to the CASCADE setting on the foreign key, 
    // deleting the invoice will automatically delete its lines
    const { error } = await typedSupabase
      .from("booking_sales_invoice")
      .delete()
      .eq("id", id);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error("Error deleting invoice:", error);
    return { success: false, error };
  }
};

/**
 * Fetch pricing components for invoice creation
 */
export const fetchPricingComponents = async () => {
  try {
    const { data, error } = await typedSupabase
      .from("pricing_components")
      .select("*")
      .order("name");

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error("Error fetching pricing components:", error);
    return { data: [], error };
  }
};

 